import {
	GraphQLResultsCompleteTestRunOutput,
	GraphQLResultsTestResultOutput,
	GraphQLResultsTestRunModelOutput,
	GraphQLResultsTestRunModelRunOutput,
	GraphQLResultsTestRunModelResultOutput,
} from 'src/app/graphql/models/output/results-complete-test-run.output.model';
import {
	ResultsCompleteTestRun,
	ResultsTestRunModel,
	ResultsTestRun,
	ResultsTestResultSummary,
	ResultsTestSuiteRef,
	ResultsTestSpecificationRef,
	ResultsEcuSwIdentifier,
} from 'src/app/models/results';

/**
 * Maps the raw GraphQL output to the app-level ResultsCompleteTestRun model.
 * Numeric enums (classification, classificationReason, status) are converted
 * to human-readable strings at this boundary.
 * @param raw
 */
export function mapGraphQLResultsCompleteTestRun(raw: GraphQLResultsCompleteTestRunOutput): ResultsCompleteTestRun {
	return {
		testRun: mapTestRun(raw.testRun),
		testSuite: raw.testSuite ? mapTestSuiteRef(raw.testSuite) : null,
		testSpecification: raw.testSpecification ? mapTestSpecRef(raw.testSpecification) : null,
		ecuAndSwIdentifiers: raw.ecuAndSwIdentifiers?.map(mapEcuSw) || [],
		testRunType: raw.testRunType,
		testSuiteVersion: raw.testSuiteVersion,
	};
}

/**
 * Maps a raw resultsTestRunModelByDates entry to the app-level ResultsTestRunModel.
 *
 * Unlike mapGraphQLResultsCompleteTestRun, classification/status are NOT converted to labels here —
 * the downstream flat-document mapper resolves those enums itself, so the raw numeric values are
 * preserved (as strings) to avoid a double conversion.
 * @param raw
 */
export function mapGraphQLResultsTestRunModel(raw: GraphQLResultsTestRunModelOutput): ResultsTestRunModel {
	return {
		testRun: mapTestRunModelRun(raw.testRun),
		testSuite: raw.testSuite ? mapTestSuiteRef(raw.testSuite) : null,
		testSpecification: raw.testSpecification ? mapTestSpecRef(raw.testSpecification) : null,
		ecuAndSwIdentifiers: (raw.ecuAndSwIdentifiers ?? []).map(mapEcuSw),
		testRunType: raw.testRunType,
		testSuiteVersion: raw.testSuiteVersion,
	};
}

export function mapGraphQLResultsTestRunModels(raw: GraphQLResultsTestRunModelOutput[] | null | undefined): ResultsTestRunModel[] {
	return (raw ?? []).map(mapGraphQLResultsTestRunModel);
}

function mapTestRunModelRun(raw: GraphQLResultsTestRunModelRunOutput): ResultsTestRun {
	return {
		testRunUid: raw.testRunUid,
		executionTime: raw.executionTime,
		environment: raw.environment,
		vinNo: raw.vinNo,
		configuration: raw.configuration,
		sop: raw.sop,
		testPeriod: raw.testPeriod,
		classification: raw.classification != null ? String(raw.classification) : '',
		classificationReason: raw.classificationReason,
		classificationComment: raw.classificationComment,
		status: raw.status != null ? String(raw.status) : '',
		regBy: raw.regBy,
		regTime: raw.regTime,
		testResults: (raw.testResults ?? []).map(mapTestRunModelResult),
	};
}

function mapTestRunModelResult(raw: GraphQLResultsTestRunModelResultOutput): ResultsTestResultSummary {
	return {
		testResultUid: raw.testResultUid,
		result: raw.result,
		resultComment: raw.resultComment,
		inputType: null,
		positiveComment: '',
		negativeComment: '',
		testCaseUid: raw.testCaseUid,
		testCaseItemNo: '',
		testCaseName: raw.testCaseName,
		testCaseVersion: raw.testCaseVersion,
		testSuiteUid: raw.testSuiteUid,
		testSuiteName: raw.testSuiteName,
		testSuiteItemNo: '',
		testSpecificationUid: '',
		testSpecificationName: '',
		testSpecificationItemNo: '',
		failedReason: raw.failedReason,
		failedReasonText: '',
		failedBy: raw.failedBy,
		failedReasonComment: raw.failedReasonComment,
		testCaseAlternativeFailedReasons: null,
		troubleReports: null,
	};
}

function mapTestRun(raw: GraphQLResultsCompleteTestRunOutput['testRun']): ResultsTestRun {
	return {
		testRunUid: raw.testRunUid,
		executionTime: raw.executionTime,
		environment: raw.environment,
		vinNo: raw.vinNo,
		configuration: raw.configuration,
		sop: raw.sop,
		testPeriod: raw.testPeriod,
		classification: mapClassification(raw.classification),
		classificationReason: raw.classificationReason,
		classificationComment: raw.classificationComment,
		status: mapTestRunStatus(raw.status),
		regBy: raw.regBy,
		regTime: raw.regTime,
		testResults: (raw.testResults || []).map(mapTestResult),
	};
}

function mapTestResult(raw: GraphQLResultsTestResultOutput): ResultsTestResultSummary {
	return {
		testResultUid: raw.testResultUid,
		result: raw.result,
		resultComment: raw.resultComment,
		inputType: raw.inputType,
		positiveComment: raw.positiveComment,
		negativeComment: raw.negativeComment,
		testCaseUid: raw.testCaseUid,
		testCaseItemNo: raw.testCaseItemNo,
		testCaseName: raw.testCaseName,
		testCaseVersion: raw.testCaseVersion,
		testSuiteUid: raw.testSuiteUid,
		testSuiteName: raw.testSuiteName,
		testSuiteItemNo: raw.testSuiteItemNo,
		testSpecificationUid: raw.testSpecificationUid,
		testSpecificationName: raw.testSpecificationName,
		testSpecificationItemNo: raw.testSpecificationItemNo,
		failedReason: raw.failedReason,
		failedReasonText: raw.failedReasonText,
		failedBy: raw.failedBy,
		failedReasonComment: raw.failedReasonComment,
		testCaseAlternativeFailedReasons: raw.testCaseAlternativeFailedReasons,
		troubleReports: raw.troubleReports,
	};
}

function mapTestSuiteRef(raw: { testSuiteUid: string; itemNo: string; name: string; version: number }): ResultsTestSuiteRef {
	return { testSuiteUid: raw.testSuiteUid, itemNo: raw.itemNo, name: raw.name, version: raw.version };
}

function mapTestSpecRef(raw: { uid: string; itemNo: string; name: string; version: number }): ResultsTestSpecificationRef {
	return { uid: raw.uid, itemNo: raw.itemNo, name: raw.name, version: raw.version };
}

function mapEcuSw(raw: { name: string; swIdentifiers: string }): ResultsEcuSwIdentifier {
	return { name: raw.name, swIdentifiers: raw.swIdentifiers };
}

function mapClassification(value: unknown): string {
	if (value === 0 || value === '0') return 'Valid';
	if (value === 1 || value === '1') return 'NotValid';
	return '';
}

function mapTestRunStatus(value: unknown): string {
	if (value === 0 || value === '0') return 'Draft';
	if (value === 1 || value === '1') return 'Final';
	return '';
}

/**
 * Returns the display label for a classificationReason number.
 * 0 = OK (no reason), 1 = Environment, 2 = Script
 * @param value
 */
export function getClassificationReasonLabel(value: number): string | null {
	if (value === 1) return 'Environment';
	if (value === 2) return 'Script';
	return null;
}
